import React, { useState, useMemo, useCallback, memo } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { apiError } from "../lib/apiError";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Upload, ArrowLeft, X, Loader2, Target, Scale, Ruler, Eye, EyeOff } from "lucide-react";
import { PhoneInput } from "react-international-phone";
import "react-international-phone/style.css";
import { MultiSelect } from "../components/ui/multi-select";
import { Switch } from "../components/ui/switch";
import { Slider } from "../components/ui/slider";
import { Badge } from "../components/ui/badge";
import { COUNTRIES, getStatesForCountry, getStateLabel } from "../lib/locationData";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PHOTO_MAX_MB = parseInt(process.env.REACT_APP_PHOTO_MAX_MB || "5", 10);

const goals = ["Muscle Building", "Weight Loss", "Weight Gain", "General Fitness", "Other"];
const relations = ["Father", "Mother", "Spouse", "Brother", "Sister", "Friend", "Other"];

const GOAL_TYPES = [
  { value: "Weight Loss", label: "Weight Loss" },
  { value: "Weight Gain", label: "Weight Gain" },
  { value: "Muscle Building", label: "Muscle Building" },
  { value: "General Fitness", label: "General Fitness" },
  { value: "Custom", label: "Custom" },
];

const MEASUREMENTS = [
  { id: "weight", label: "Weight", unit: "kg", icon: Scale },
  { id: "chest", label: "Chest", unit: "cm", icon: Ruler },
  { id: "waist", label: "Waist", unit: "cm", icon: Ruler },
  { id: "arm", label: "Arm", unit: "cm", icon: Ruler },
  { id: "bmi", label: "BMI", unit: "", icon: Scale },
];

const DAYS = Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, "0"));
const MONTHS = [
  { v: "01", l: "January" }, { v: "02", l: "February" }, { v: "03", l: "March" },
  { v: "04", l: "April" }, { v: "05", l: "May" }, { v: "06", l: "June" },
  { v: "07", l: "July" }, { v: "08", l: "August" }, { v: "09", l: "September" },
  { v: "10", l: "October" }, { v: "11", l: "November" }, { v: "12", l: "December" },
];
const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: currentYear - 1919 }, (_, i) => String(currentYear - i));

const toNumOrNull = (v) => (v === "" || v === null || v === undefined) ? null : Number(v);

// Static Options maps to prevent re-evaluation
const DAY_OPTIONS = DAYS.map((d) => ({ value: d, label: d }));
const MONTH_OPTIONS = MONTHS.map((m) => ({ value: m.v, label: m.l }));
const YEAR_OPTIONS = YEARS.map((y) => ({ value: y, label: y }));
const COUNTRY_OPTIONS = COUNTRIES.map((c) => ({ value: c, label: c }));
const RELATION_OPTIONS = relations.map((r) => ({ value: r, label: r }));

// Memoized inputs and select dropdowns to prevent unnecessary renders while typing
const GenderSelect = memo(({ value, onChange }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="rounded-sm mt-1" data-testid="member-gender"><SelectValue /></SelectTrigger>
    <SelectContent>
      <SelectItem value="Male">Male</SelectItem>
      <SelectItem value="Female">Female</SelectItem>
      <SelectItem value="Other">Other</SelectItem>
    </SelectContent>
  </Select>
));
GenderSelect.displayName = "GenderSelect";

const DobSelect = memo(({ value, onChange, placeholder, options, testId }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="rounded-sm" data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent>
      {options.map((opt) => (
        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
      ))}
    </SelectContent>
  </Select>
));
DobSelect.displayName = "DobSelect";

const CountrySelect = memo(({ value, onChange }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="rounded-sm mt-1" data-testid="member-country"><SelectValue /></SelectTrigger>
    <SelectContent>
      {COUNTRY_OPTIONS.map((c) => (
        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
      ))}
    </SelectContent>
  </Select>
));
CountrySelect.displayName = "CountrySelect";

const StateSelect = memo(({ value, onChange, country }) => {
  const states = getStatesForCountry(country);
  const label = getStateLabel(country);
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="rounded-sm mt-1" data-testid="member-state"><SelectValue placeholder={`Select ${label}`} /></SelectTrigger>
      <SelectContent>
        {states.map((s) => (
          <SelectItem key={s} value={s}>{s}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
});
StateSelect.displayName = "StateSelect";

const RelationSelect = memo(({ value, onChange }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="rounded-sm mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
    <SelectContent>
      {RELATION_OPTIONS.map((r) => (
        <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
      ))}
    </SelectContent>
  </Select>
));
RelationSelect.displayName = "RelationSelect";

const GoalTypeSelect = memo(({ value, onChange }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="rounded-sm mt-1" data-testid="goal-type-select"><SelectValue /></SelectTrigger>
    <SelectContent>
      {GOAL_TYPES.map((t) => (
        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
      ))}
    </SelectContent>
  </Select>
));
GoalTypeSelect.displayName = "GoalTypeSelect";

const MemoizedPhoneInput = memo(({ value, onChange, ...props }) => (
  <PhoneInput
    value={value}
    onChange={onChange}
    {...props}
  />
));
MemoizedPhoneInput.displayName = "MemoizedPhoneInput";

const MemoizedMultiSelect = memo(({ options, selected, onChange, placeholder }) => (
  <MultiSelect
    options={options}
    selected={selected}
    onChange={onChange}
    placeholder={placeholder}
  />
));
MemoizedMultiSelect.displayName = "MemoizedMultiSelect";


export default function MemberCreate() {
  const { currentBranch, branches } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dobDay, setDobDay] = useState("");
  const [dobMonth, setDobMonth] = useState("");
  const [dobYear, setDobYear] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [form, setForm] = useState({
    name: "", phone: "", email: "", gender: "Male", address: "", state: "", city: "",
    pincode: "", country: "India", photo: "",
    emergencyContactName: "", emergencyContactRelation: "", emergencyContactPhone: "",
    medicalIssue: "", goal: "General Fitness", height: "", weight: "",
    professional: "", reference: "",
    joiningDate: new Date().toISOString().slice(0, 10),
    notes: "", password: "",
    // Default status is New (no membership assigned yet)
    status: "New",
    branchId: currentBranch?.id || "",
    branchIds: currentBranch?.id ? [currentBranch.id] : [],
  });

  const [createGoalEnabled, setCreateGoalEnabled] = useState(false);
  const [goalForm, setGoalForm] = useState({
    name: "",
    description: "",
    goalType: "Weight Loss",
    reminderDays: 7,
  });
  const [selectedMeasurements, setSelectedMeasurements] = useState([]);

  const branchOptions = useMemo(() => {
    return branches ? branches.map(b => ({ label: b.name, value: b.id })) : [];
  }, [branches]);

  const update = useCallback((key, val) => {
    setForm((p) => {
      const updated = { ...p, [key]: val };
      if (key === "weight") {
        const weightVal = parseFloat(val) || 0;
        setSelectedMeasurements(prev => {
          const hasWeight = prev.some(m => m.metric === "weight");
          if (hasWeight) {
            return prev.map(m => m.metric === "weight" ? { ...m, currentValue: weightVal } : m);
          } else if (weightVal > 0 && createGoalEnabled) {
            return [...prev, { metric: "weight", currentValue: weightVal, targetValue: 0 }];
          }
          return prev;
        });
      }
      return updated;
    });
  }, [createGoalEnabled]);

  const handleGoalToggle = (checked) => {
    setCreateGoalEnabled(checked);
    if (checked) {
      const initialWeight = parseFloat(form.weight);
      if (initialWeight && !selectedMeasurements.some(m => m.metric === "weight")) {
        setSelectedMeasurements(prev => [
          ...prev,
          { metric: "weight", currentValue: initialWeight, targetValue: 0 }
        ]);
      }
    }
  };

  const isBusy = loading || uploading;

  const handlePhoto = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > PHOTO_MAX_MB * 1024 * 1024) {
      toast.error(`Photo must be under ${PHOTO_MAX_MB} MB.`);
      return;
    }
    setPhotoPreview(URL.createObjectURL(file));
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    if (form.photo) fd.append("old_url", form.photo);
    try {
      const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      update("photo", res.data.url);
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(apiError(err, "Photo upload failed"));
      setPhotoPreview(null);
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = () => { setPhotoPreview(null); update("photo", ""); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name) { toast.error("Name is required"); return; }
    if (!form.phone) { toast.error("Phone is required"); return; }
    if (!form.password) { toast.error("Password is required"); return; }
    if (!dobDay || !dobMonth || !dobYear) { toast.error("Date of birth is required"); return; }
    if (branches && branches.length > 0 && (!form.branchIds || form.branchIds.length === 0)) {
      toast.error("At least one branch must be selected");
      return;
    }
    const dob = `${dobYear}-${dobMonth}-${dobDay}`;
    if (new Date(dob) > new Date()) { toast.error("Date of birth cannot be in the future"); return; }

    if (createGoalEnabled) {
      if (!goalForm.name.trim()) {
        toast.error("Goal name is required");
        return;
      }
      if (selectedMeasurements.length === 0) {
        toast.error("Select at least one measurement to track for the goal");
        return;
      }
    }

    setLoading(true);
    try {
      const payload = {
        ...form,
        dob,
        height: toNumOrNull(form.height),
        weight: toNumOrNull(form.weight),
      };
      const res = await api.post("/members", payload);

      if (createGoalEnabled) {
        try {
          const goalPayload = {
            memberId: res.data.member.id,
            branchId: payload.branchId || res.data.member.branchId || "",
            name: goalForm.name.trim(),
            description: goalForm.description,
            goalType: goalForm.goalType,
            reminderDays: goalForm.reminderDays,
            measurements: selectedMeasurements,
          };
          await api.post("/goals", goalPayload);
        } catch (goalErr) {
          toast.error("Member created, but failed to set goal: " + (goalErr.response?.data?.detail || goalErr.message));
        }
      }

      toast.success("Member created successfully!");
      navigate(`/members/${res.data.member.id}`);
    } catch (err) {
      toast.error(apiError(err, "Failed to create member"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="member-create-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate("/members")} className="rounded-sm" data-testid="back-btn" disabled={isBusy}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h1 className="text-2xl font-bold font-heading tracking-tight">Add New Member</h1>
      </div>

      <form onSubmit={handleSubmit} className={isBusy ? "opacity-60 pointer-events-none select-none" : ""}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

          {/* Personal Info */}
          <Card className="bg-card border-border rounded-sm">
            <CardHeader className="p-3 md:px-6"><CardTitle className="text-lg font-heading">Personal Info</CardTitle></CardHeader>
            <CardContent className="space-y-4 p-3 md:px-6">
              <div className="flex items-center gap-2 md:gap-4 mb-4">
                <div className="relative w-20 h-20 rounded-sm bg-secondary flex items-center justify-center overflow-hidden border border-border">
                  {photoPreview || form.photo
                    ? <img src={photoPreview || `${BACKEND_URL}${form.photo}`} alt="" className="w-full h-full object-cover" />
                    : <Upload className="h-6 w-6 text-muted-foreground" />}
                  {(photoPreview || form.photo) && (
                    <button type="button" onClick={removePhoto}
                      className="absolute top-0.5 right-0.5 bg-black/60 rounded-full p-0.5 text-white hover:bg-black/80">
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </div>
                <div className="space-y-1 gap-2 md:gap-4">
                  <Label htmlFor="photo" className={`cursor-pointer text-primary text-sm hover:underline ${uploading ? "opacity-50 pointer-events-none" : ""}`}>
                    {uploading ? "Uploading…" : (form.photo ? "Change Photo" : "Upload Photo")}
                  </Label>
                  <p className="text-xs text-muted-foreground">Max {PHOTO_MAX_MB} MB · JPG / PNG</p>
                  <input id="photo" type="file" accept="image/*" className="hidden" onChange={handlePhoto} data-testid="photo-upload" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-4">
                <div>
                  <Label>Name *</Label>
                  <Input value={form.name} onChange={(e) => update("name", e.target.value)} required className="rounded-sm mt-1" data-testid="member-name" />
                </div>
                <div>
                  <Label>Phone *</Label>
                  <MemoizedPhoneInput defaultCountry="in" value={form.phone}
                    onChange={(v) => update("phone", v || "")}
                    inputClassName="flex h-10 w-full rounded-sm border border-input bg-secondary px-3 py-2 text-sm focus-within:ring-2 focus-within:ring-primary/30"
                    className="mt-1"
                    data-testid="member-phone" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Email</Label>
                  <Input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} className="rounded-sm mt-1" data-testid="member-email" />
                </div>
                <div>
                  <Label>Gender</Label>
                  <GenderSelect value={form.gender} onChange={(v) => update("gender", v)} />
                </div>
              </div>

              <div>
                <Label>Date of Birth *</Label>
                <div className="grid grid-cols-3 gap-2 mt-1">
                  <DobSelect value={dobDay} onChange={setDobDay} placeholder="Day" options={DAY_OPTIONS} testId="dob-day" />
                  <DobSelect value={dobMonth} onChange={setDobMonth} placeholder="Month" options={MONTH_OPTIONS} testId="dob-month" />
                  <DobSelect value={dobYear} onChange={setDobYear} placeholder="Year" options={YEAR_OPTIONS} testId="dob-year" />
                </div>
              </div>

              <div>
                <Label>Joining Date</Label>
                <Input type="date" value={form.joiningDate} onChange={(e) => update("joiningDate", e.target.value)} className="rounded-sm mt-1" data-testid="member-joining" />
              </div>

              <div>
                <Label>Password (Member Login) *</Label>
                <div className="relative mt-1">
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={form.password}
                    onChange={(e) => update("password", e.target.value)}
                    required
                    className="rounded-sm pr-10"
                    data-testid="member-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {branches && branches.length > 0 && (
                <div>
                  <Label className="flex items-center gap-1.5 mb-1.5">Select Branches *</Label>
                  <MemoizedMultiSelect
                    options={branchOptions}
                    selected={form.branchIds || []}
                    onChange={(vals) => {
                      update("branchIds", vals);
                      update("branchId", vals[0] || "");
                    }}
                    placeholder="Select branches..."
                  />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Address */}
          <Card className="bg-card border-border rounded-sm">
            <CardHeader className="p-3 md:px-6"><CardTitle className="text-lg font-heading">Address</CardTitle></CardHeader>
            <CardContent className="space-y-4 p-3 md:px-6">
              <div>
                <Label>Address</Label>
                <Textarea value={form.address} onChange={(e) => update("address", e.target.value)} className="rounded-sm mt-1" data-testid="member-address" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{getStateLabel(form.country)}</Label>
                  {getStatesForCountry(form.country).length > 0 ? (
                    <StateSelect value={form.state} onChange={(v) => update("state", v)} country={form.country} />
                  ) : (
                    <Input value={form.state} onChange={(e) => update("state", e.target.value)} className="rounded-sm mt-1" placeholder={`Enter ${getStateLabel(form.country)}`} data-testid="member-state" />
                  )}
                </div>
                <div>
                  <Label>City</Label>
                  <Input value={form.city} onChange={(e) => update("city", e.target.value)} className="rounded-sm mt-1" data-testid="member-city" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Pincode</Label>
                  <Input type="number" inputMode="numeric" value={form.pincode}
                    onChange={(e) => update("pincode", e.target.value)} className="rounded-sm mt-1" />
                </div>
                <div>
                  <Label>Country</Label>
                  <CountrySelect value={form.country} onChange={(v) => { update("country", v); update("state", ""); }} />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Emergency Contact */}
          <Card className="bg-card border-border rounded-sm">
            <CardHeader className="p-3 md:px-6"><CardTitle className="text-lg font-heading">Emergency Contact</CardTitle></CardHeader>
            <CardContent className="space-y-4 p-3 md:px-6">
              <div>
                <Label>Contact Name</Label>
                <Input value={form.emergencyContactName} onChange={(e) => update("emergencyContactName", e.target.value)} className="rounded-sm mt-1" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Relation</Label>
                  <RelationSelect value={form.emergencyContactRelation} onChange={(v) => update("emergencyContactRelation", v)} />
                </div>
                <div>
                  <Label>Phone</Label>
                  <MemoizedPhoneInput defaultCountry="in" value={form.emergencyContactPhone}
                    onChange={(v) => update("emergencyContactPhone", v || "")}
                    inputClassName="flex h-10 w-full rounded-sm border border-input bg-secondary px-3 py-2 text-sm focus-within:ring-2 focus-within:ring-primary/30"
                    className="mt-1" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Fitness & Health */}
          <Card className="bg-card border-border rounded-sm">
            <CardHeader className="p-3 md:px-6"><CardTitle className="text-lg font-heading">Fitness & Health</CardTitle></CardHeader>
            <CardContent className="space-y-4 p-3 md:px-6">
              <div className="flex items-center justify-between p-3 bg-secondary/30 rounded-sm border border-border">
                <div className="space-y-0.5">
                  <Label className="text-sm font-semibold flex items-center gap-2">
                    <Target className="h-4 w-4 text-primary" />
                    Set Fitness Goal
                  </Label>
                  <p className="text-xs text-muted-foreground">Create an initial goal with target metrics for this member</p>
                </div>
                <Switch
                  checked={createGoalEnabled}
                  onCheckedChange={handleGoalToggle}
                  type="button"
                  data-testid="create-goal-toggle"
                />
              </div>

              {createGoalEnabled && (
                <div className="space-y-4 pt-2 border-t border-border/60 animate-in fade-in duration-200">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Goal Name *</Label>
                      <Input
                        placeholder="e.g. Lose 10kg"
                        value={goalForm.name}
                        onChange={(e) => setGoalForm({ ...goalForm, name: e.target.value })}
                        className="rounded-sm mt-1"
                        data-testid="goal-name-input"
                      />
                    </div>
                    <div>
                      <Label>Goal Type</Label>
                      <GoalTypeSelect value={goalForm.goalType} onChange={(v) => setGoalForm({ ...goalForm, goalType: v })} />
                    </div>
                  </div>

                  <div>
                    <Label>Description</Label>
                    <Textarea
                      placeholder="Add details about this goal..."
                      value={goalForm.description}
                      onChange={(e) => setGoalForm({ ...goalForm, description: e.target.value })}
                      className="rounded-sm mt-1 min-h-[60px]"
                    />
                  </div>

                  {/* Measurement selections */}
                  <div className="space-y-2">
                    <Label className="text-xs font-mono uppercase tracking-wide text-muted-foreground">
                      Select Measurements to Track
                    </Label>
                    <div className="flex flex-wrap gap-2">
                      {MEASUREMENTS.map(m => {
                        const isSelected = selectedMeasurements.some(sm => sm.metric === m.id);
                        return (
                          <Badge
                            key={m.id}
                            variant={isSelected ? "default" : "outline"}
                            className={`cursor-pointer px-3 py-1.5 rounded-sm ${isSelected ? 'bg-primary text-primary-foreground' : ''}`}
                            onClick={() => {
                              const exists = selectedMeasurements.find(sm => sm.metric === m.id);
                              if (exists) {
                                setSelectedMeasurements(selectedMeasurements.filter(sm => sm.metric !== m.id));
                              } else {
                                setSelectedMeasurements([...selectedMeasurements, {
                                  metric: m.id,
                                  currentValue: m.id === "weight" ? (parseFloat(form.weight) || 70) : 0,
                                  targetValue: 0,
                                }]);
                              }
                            }}
                          >
                            <m.icon className="h-3 w-3 mr-1" />
                            {m.label}
                            {isSelected && <X className="h-3 w-3 ml-1" />}
                          </Badge>
                        );
                      })}
                    </div>
                  </div>

                  {/* Measurement Values input */}
                  {selectedMeasurements.length > 0 && (
                    <div className="space-y-3">
                      <Label className="text-xs font-mono uppercase tracking-wide text-muted-foreground">
                        Set Target Values
                      </Label>
                      <div className="space-y-2">
                        {selectedMeasurements.map(sm => {
                          const mInfo = MEASUREMENTS.find(m => m.id === sm.metric);
                          return (
                            <div key={sm.metric} className="grid grid-cols-3 gap-2 p-2 bg-secondary/20 border border-border/40 rounded-sm items-center">
                              <div>
                                <p className="text-xs font-medium flex items-center gap-1.5">
                                  <mInfo.icon className="h-3.5 w-3.5 text-primary" />
                                  {mInfo.label} {mInfo.unit ? `(${mInfo.unit})` : ''}
                                </p>
                              </div>
                              <div className="flex items-center gap-1.5">
                                <Label className="text-[10px] text-muted-foreground font-mono uppercase shrink-0">Cur</Label>
                                <Input
                                  type="number"
                                  step="0.1"
                                  value={sm.currentValue}
                                  onChange={(e) => {
                                    const val = parseFloat(e.target.value) || 0;
                                    setSelectedMeasurements(selectedMeasurements.map(item =>
                                      item.metric === sm.metric ? { ...item, currentValue: val } : item
                                    ));
                                  }}
                                  className="h-8 rounded-sm text-xs"
                                />
                              </div>
                              <div className="flex items-center gap-1.5">
                                <Label className="text-[10px] text-muted-foreground font-mono uppercase shrink-0">Tgt</Label>
                                <Input
                                  type="number"
                                  step="0.1"
                                  value={sm.targetValue}
                                  onChange={(e) => {
                                    const val = parseFloat(e.target.value) || 0;
                                    setSelectedMeasurements(selectedMeasurements.map(item =>
                                      item.metric === sm.metric ? { ...item, targetValue: val } : item
                                    ));
                                  }}
                                  className="h-8 rounded-sm text-xs font-semibold border-primary/40 focus:border-primary"
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Reminder Days */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs font-mono uppercase tracking-wide text-muted-foreground">
                        Progress Reminder
                      </Label>
                      <Badge variant="outline" className="font-mono text-[10px] rounded-sm py-0">
                        Every {goalForm.reminderDays} days
                      </Badge>
                    </div>
                    <Slider
                      value={[goalForm.reminderDays]}
                      onValueChange={(v) => setGoalForm({ ...goalForm, reminderDays: v[0] })}
                      min={1}
                      max={31}
                      step={1}
                      className="w-full py-2"
                    />
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Height (cm)</Label>
                  <Input type="number" inputMode="decimal" min={0} step="0.1"
                    value={form.height} onChange={(e) => update("height", e.target.value)}
                    placeholder="Optional" className="rounded-sm mt-1" />
                </div>
                <div>
                  <Label>Weight (kg)</Label>
                  <Input type="number" inputMode="decimal" min={0} step="0.1"
                    value={form.weight} onChange={(e) => update("weight", e.target.value)}
                    placeholder="Optional" className="rounded-sm mt-1" />
                </div>
              </div>
              <div>
                <Label>Medical Issue</Label>
                <Textarea value={form.medicalIssue} onChange={(e) => update("medicalIssue", e.target.value)} className="rounded-sm mt-1" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Professional</Label>
                  <Input value={form.professional} onChange={(e) => update("professional", e.target.value)} className="rounded-sm mt-1" />
                </div>
                <div>
                  <Label>Reference</Label>
                  <Input value={form.reference} onChange={(e) => update("reference", e.target.value)} className="rounded-sm mt-1" />
                </div>
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea value={form.notes} onChange={(e) => update("notes", e.target.value)} className="rounded-sm mt-1" />
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <Button variant="outline" type="button" onClick={() => navigate("/members")}
            className="rounded-sm" data-testid="cancel-btn" disabled={isBusy}>
            Cancel
          </Button>
          <Button type="submit" disabled={isBusy} className="rounded-sm min-w-[130px]" data-testid="save-member-btn">
            {loading ? (
              <span className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Saving…
              </span>
            ) : "Save Member"}
          </Button>
        </div>
      </form>
    </div>
  );
}
